import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "../context/ContentContext";
import ScrollCue from "./ScrollCue";
import SEO from "./SEO";

const InlineVideo = ({ src }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) v.play().catch(() => {});
          else v.pause();
        });
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      src={src}
      className="w-full h-full object-contain"
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
    />
  );
};

const Capabilities = () => {
  const { CAPABILITIES, CAPABILITIES_HERO_VIDEO, BRAND } = useContent();
  const heroRef = useRef(null);

  useEffect(() => {
    const v = heroRef.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, []);

  return (
    <div className="bg-canvas text-ink min-h-screen">
      <SEO
        title="Capabilities · Hybrid, AI-Led & Content Engines | SAMURAI"
        description="Three ways SAMURAI works: Hybrid Production combining live capture with AI, fully AI-led Production, and custom Content Engines for on-brand scale."
        path="/capabilities"
      />
      {/* Full viewport hero, mirrors landing page */}
      <section className="dark-block relative min-h-[86vh] md:min-h-[92vh] flex items-end overflow-hidden pt-24 bg-canvas">
        <div className="absolute inset-0">
          <video
            ref={heroRef}
            src={CAPABILITIES_HERO_VIDEO}
            className="hero-video absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          <div className="absolute inset-0 hero-scrim" />
          <div className="absolute inset-0 pointer-events-none noise-overlay" />
        </div>

        <div className="relative mx-auto px-6 md:px-10 w-full pb-16 md:pb-24">
          <h1 className="font-heading font-black text-[40px] md:text-[72px] leading-[0.92] tracking-[-0.03em] uppercase fade-up">
            Three{" "}
            <span className="italic font-serif font-normal text-ink/85 normal-case">
              pipelines.
            </span>{" "}
            One studio.
          </h1>
        </div>

        <ScrollCue right={BRAND.locations} />
      </section>

      {/* Capability rows */}
      <section className="py-4 border-t border-ink/10">
        <div className="mx-auto px-6 md:px-10 flex flex-col divide-y divide-ink/10">
          {CAPABILITIES.map((cap, idx) => (
            <Link
              key={cap.id}
              to={`/capabilities/${cap.id}`}
              data-testid={`capability-row-${cap.id}`}
              className="group py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative overflow-hidden cursor-pointer transition-colors hover:bg-ink/[0.015]"
            >
              <span className="absolute right-0 -top-4 text-[160px] md:text-[260px] font-heading font-black text-ink/[0.045] leading-none pointer-events-none select-none">
                {cap.number}
              </span>

              <div
                className={`md:col-span-5 ${
                  idx % 2 === 1 ? "md:order-2" : ""
                }`}
              >
                <div className="relative overflow-hidden rounded-2xl aspect-video bg-black max-w-[520px] mx-auto md:mx-0">
                  {cap.video ? (
                    <InlineVideo src={cap.video} />
                  ) : (
                    <img
                      src={cap.image}
                      alt={cap.title}
                      className="w-full h-full object-contain"
                    />
                  )}
                  <span className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase bg-black/50 backdrop-blur px-2 py-1 rounded-full">
                    {cap.number}
                  </span>
                </div>
              </div>

              <div className="md:col-span-7 md:pl-6 relative">
                <p className="text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-3">
                  {cap.number}
                </p>
                <h2 className="font-heading font-black text-[28px] md:text-[48px] tracking-[-0.02em] leading-[1.02] uppercase">
                  {cap.title}
                </h2>
                {cap.subtitle && (
                  <p className="mt-3 font-serif italic text-lg md:text-2xl text-ink/85 leading-snug">
                    {cap.subtitle}
                  </p>
                )}
                <p className="mt-5 text-sm md:text-base text-ink/75 leading-relaxed max-w-lg">
                  {cap.description}
                </p>
                <span className="mt-7 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-ink/70 group-hover:text-ink transition-colors">
                  Explore
                  <span className="w-9 h-9 rounded-full border border-ink/30 group-hover:border-ink flex items-center justify-center transition-all duration-500 group-hover:rotate-45">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Capabilities;
